import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, today, EXPENSE_PAY_METHODS } from "@/lib/api";
import { Field, inputCls, selectCls, textareaCls } from "@/components/common";

export function ExpenseDialog({ open, onClose, camera = false, expense = null }) {
  const qc = useQueryClient();
  const { data: cats = [] } = useQuery({ queryKey: ["expense-categories"], queryFn: () => api.get("/expense-categories").then((r) => r.data), enabled: open });
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const camRef = useRef();
  const fileRef = useRef();

  useEffect(() => {
    if (!open) return;
    setForm(expense ? { ...expense } : { amount: "", category: "", merchant: "", payment_method: "Business Debit", date: today(), description: "" });
    setFile(null); setPreview(null);
    if (camera) setTimeout(() => camRef.current?.click(), 250);
  }, [open, camera, expense]);

  const pick = (f) => { if (!f) return; setFile(f); setPreview(URL.createObjectURL(f)); };
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.category) return toast.error("Amount and category are required");
    setSaving(true);
    try {
      const fd = new FormData();
      ["amount", "category", "merchant", "payment_method", "date", "description"].forEach((k) => fd.append(k, form[k] ?? ""));
      if (file) fd.append("receipt", file);
      if (expense) await api.put(`/expenses/${expense.id}`, fd); else await api.post("/expenses", fd);
      toast.success(expense ? "Expense updated" : "Expense saved");
      qc.invalidateQueries();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{expense ? "Edit Expense" : "New Expense"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount"><input data-testid="expense-amount-input" type="number" step="0.01" min="0" inputMode="decimal" className={inputCls + " text-lg font-semibold"} value={form.amount ?? ""} onChange={set("amount")} placeholder="0.00" required /></Field>
            <Field label="Date"><input data-testid="expense-date-input" type="date" className={inputCls} value={form.date || ""} onChange={set("date")} /></Field>
          </div>
          <Field label="Category">
            <select data-testid="expense-category-select" className={selectCls} value={form.category || ""} onChange={set("category")} required>
              <option value="">Select category</option>
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Merchant"><input data-testid="expense-merchant-input" className={inputCls} value={form.merchant || ""} onChange={set("merchant")} placeholder="Home Depot" /></Field>
            <Field label="Paid with">
              <select data-testid="expense-method-select" className={selectCls} value={form.payment_method || ""} onChange={set("payment_method")}>
                {EXPENSE_PAY_METHODS.map((m) => <option key={m}>{m}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description"><textarea data-testid="expense-description-input" className={textareaCls + " min-h-[70px]"} value={form.description || ""} onChange={set("description")} placeholder="What was this for?" /></Field>
          <div>
            <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Receipt photo</span>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="expense-receipt-camera-input" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="expense-receipt-file-input" />
            {preview || (expense?.receipt_file_id && !file) ? (
              <div className="relative inline-block">
                <img src={preview || `${api.defaults.baseURL}/files/${expense.receipt_file_id}`} alt="Receipt" className="h-36 rounded-lg border object-cover" data-testid="expense-receipt-preview" />
                {file && <button type="button" onClick={() => { setFile(null); setPreview(null); }} className="absolute -top-2 -right-2 bg-white border rounded-full p-1 shadow"><X size={14} /></button>}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => camRef.current?.click()} data-testid="expense-take-photo-button" className="h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50 flex flex-col items-center justify-center gap-1 text-sm font-medium text-slate-600 transition-colors"><Camera size={20} className="text-orange-500" />Take photo</button>
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="expense-upload-photo-button" className="h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50 flex flex-col items-center justify-center gap-1 text-sm font-medium text-slate-600 transition-colors"><Upload size={20} className="text-orange-500" />Upload</button>
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1 h-11" onClick={onClose} data-testid="expense-cancel-button">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 h-11 btn-orange" data-testid="expense-save-button">{saving ? "Saving..." : "Save Expense"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
